import { LichtblickSession, Maybe } from '@lichtblick/types';

export const downloadWorkOrderFolder = async (
  session: LichtblickSession | null,
  workOrderId: number,
  folderId: number | null | undefined,
  fileName?: string,
  mediaIds?: number[],
) => {
  const newUri = mediaIds?.length
    ? `/files/workOrder/${workOrderId}/download`
    : `/files/workOrder/${workOrderId}/folder/${folderId}/download`;
  const fullUrl = process.env.NEXT_PUBLIC_API?.replace('/graphql', newUri);

  if (!session || !fullUrl) {
    throw new Error('No session or API URL available');
  }

  const response = await fetch(fullUrl, {
    body: mediaIds?.length ? JSON.stringify(mediaIds) : undefined,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(mediaIds?.length ? { 'Content-Type': 'application/json' } : {}),
    },
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`Failed to download folder: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName ?? 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const fetchFile = async (
  session: LichtblickSession | null,
  file: { contentType: string; url: string },
  isFile?: boolean,
  isUrl?: boolean,
) => {
  const fullUrl = isUrl ? file.url : process.env.NEXT_PUBLIC_API?.replace('/graphql', file.url);

  if (fullUrl && session) {
    const response = await fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'content-type': file.contentType,
      },
    });

    if (!response.ok) {
      // Throw an error with appropriate message and status code
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }

    if (response.ok) {
      const blob = await response.blob();

      if (isFile) {
        return blob;
      }

      const ab = await blob.arrayBuffer();
      const object = {
        data: Array.from(new Uint8Array(ab)),
        name: file.contentType,
      };

      return object;
    }

    return null;
  }

  return null;
};

export const deleteFile = async (
  session: LichtblickSession | null,
  url: Maybe<string | undefined>,
  folderId: number | undefined,
  mediaId: number,
  workOrderId: number,
) => {
  if (!folderId) {
    throw new Error(`No folderId`);
  }

  if (!url) {
    throw new Error(`No URL`);
  }

  const newUri = `/files/workOrder/${workOrderId}/folder/${folderId}/media/${mediaId}`;
  const fullUrl = process.env.NEXT_PUBLIC_API?.replace('/graphql', newUri);

  if (session && fullUrl) {
    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`);
    }

    return response.ok;
  }

  return null;
};

export const downloadFileExtend = async (uri: string, fileName: string) => {
  const response = await fetch(uri ?? '');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName ?? 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
